import { TFunction } from 'i18next';
import * as yup from 'yup';

import { BankNames, BankAccountDataField } from 'entities/bankAccounts';
import { CountryISOCodes } from 'common/countries';
import { trimStr, isSubset } from 'utils/helpers';
import {
  BankAccountCheckRangeType,
  BankAccountCheckType,
  BankAccountTypeSchema,
} from 'services/bankAccounts';
import {
  stringRequired,
  validateCity,
  validateState,
  validatePostalCode,
  validatePhone,
  validateEmail,
} from 'utils/validation';

export const getFundingAccountSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      email: validateEmail(t),
      phone: validatePhone(t),
    })
    .required();

export const getBankName = (name: string) => {
  return BankNames[name.replace(/\s/g, '')] || '';
};

export const mappedSearchAccounts = () => {
  return {
    isActive: true,
  };
};

export const mappedAccountTypes = (accountTypes: BankAccountTypeSchema[]) => {
  return accountTypes.map(({ id, name }) => ({
    name,
    value: id,
  }));
};

export const mapAdditionalFields = ({ fields, additionalFields, jsonData }) => {
  const data = fields.slice().map((field) => ({
    id: field.id,
    name: field.name,
    modifiedOn:
      trimStr(additionalFields[field.id]) ===
      jsonData[`additionalFields.${field.id}`]
        ? null
        : new Date().toISOString(),
    value: trimStr(additionalFields[field.id]),
  }));

  return data;
};

type MapCheckRangesParamsType = {
  bankAccountsId: number;
  emailAddress: string;
};

export const mapCheckRanges = (
  ranges: BankAccountCheckRangeType[],
  existedRanges: BankAccountCheckRangeType[],
  params: MapCheckRangesParamsType,
) => {
  const result: BankAccountCheckRangeType[] = ranges
    .filter(
      (range: BankAccountCheckRangeType) => range.startRange && range.endRange,
    )
    .map((range: BankAccountCheckRangeType) => {
      const rangeData: BankAccountCheckRangeType = {
        ...range,
        bankAccountsId: params.bankAccountsId,
      };

      if (!rangeData.createdBy) {
        rangeData.createdBy = params.emailAddress;
      } else {
        rangeData.modifiedBy = params.emailAddress;
      }

      return rangeData;
    });

  existedRanges.map((checkRange: BankAccountCheckRangeType) => {
    if (
      !result.find(
        (range: BankAccountCheckRangeType) =>
          range.banksAccountsChecksRangesId ===
          checkRange.banksAccountsChecksRangesId,
      )
    ) {
      result.unshift({
        ...checkRange,
        isActive: false,
        modifiedBy: params.emailAddress,
      });
    }

    return checkRange;
  });

  return result;
};

export const mapChecks = (
  omitted: BankAccountCheckType[],
  existedOmitted: BankAccountCheckType[],
  params: MapCheckRangesParamsType,
) => {
  const result: BankAccountCheckType[] = omitted.map(
    (omit: BankAccountCheckType) => {
      const omitData: BankAccountCheckType = {
        ...omit,
        banksAccountsId: params.bankAccountsId,
        banksAccountsChecksId: omit.banks_Accounts_ChecksId,
      };

      if (!omitData.createdBy) {
        omitData.createdBy = params.emailAddress;
      } else {
        omitData.modifiedBy = params.emailAddress;
      }

      return omitData;
    },
  );

  existedOmitted.map((checkOmit: BankAccountCheckType) => {
    if (
      !result.find(
        (omit: BankAccountCheckType) =>
          omit.banks_Accounts_ChecksId === checkOmit.banks_Accounts_ChecksId,
      )
    ) {
      result.unshift({
        ...checkOmit,
        banksAccountsChecksId: checkOmit.banks_Accounts_ChecksId,
        banksAccountsId: params.bankAccountsId,
        isActive: false,
        modifiedBy: params.emailAddress,
      });
    }

    return checkOmit;
  });

  return result;
};

const mapCustomFields = (data) => {
  const customFields: Record<string, string> = {};

  if (data.countryIsocode === CountryISOCodes.UnitedKingdom && data.sortCode) {
    // The input is masked (99-99-99); store the raw digits, e.g. "12-23-34" -> "123234".
    customFields.UK_SortCode = String(data.sortCode).replace(/-/g, '');
  }

  return customFields;
};

// Reads a single typed value out of the generic customFields list returned by the BE.
export const getCustomFieldValue = (
  customFields: BankAccountDataField[] | null | undefined,
  name: string,
): string | null =>
  customFields?.find((field) => field.bankAccountDataTypeName === name)
    ?.value ?? null;

export const getSortCode = (
  customFields: BankAccountDataField[] | null | undefined,
): string | null => getCustomFieldValue(customFields, 'UK_SortCode');

export const mapCreateBankAccountFields = ({
  data,
  person,
  business,
  bankAccounts,
}) => {
  const customFields = mapCustomFields(data);

  return {
    ...data,
    ...(Object.keys(customFields).length
      ? { customFields: JSON.stringify(customFields) }
      : {}),
    countryISOCode: data.countryIsocode,
    accountNumberLast4: data.accountNumber.slice(-4),
    businessesId: business.businessesId,
    banksIntegratorsId: bankAccounts.integrator?.banksIntegratorsId || 0,
    integratorDirectoryId: bankAccounts.directoryId,
    createdBy: person.emailAddress,
    userCalledName: '',
    isActive: true,
  };
};

export const isBankAccountEmpty = ({ bankAccount, integrator }) => {
  if (bankAccount?.banksIntegratorsId === 0) {
    return false;
  }

  const banksIntegratorsId = bankAccount?.banksIntegratorsId || 0;

  return (
    !bankAccount || (integrator?.banksIntegratorsId || 0) !== banksIntegratorsId
  );
};

export const mapUpdateBankAccountFields = ({
  data,
  emailAddress,
  bankAccounts,
}) => {
  const { bankAccount, integrator, directoryId } = bankAccounts;

  const { currentBankAccount, ...payload } = data;

  const currentAccount = bankAccount || currentBankAccount || null;

  if (currentAccount == null || isSubset(payload, currentAccount)) {
    return null;
  }

  const empty = isBankAccountEmpty({ bankAccount: currentAccount, integrator });
  const accountNumber = data.accountNumber ?? currentAccount.accountNumber;

  const { addressLine1, city, postalCode, stateOrProvince, countryISOCode } =
    currentAccount;

  const customFields = mapCustomFields({
    countryIsocode: payload.countryIsocode ?? currentAccount.countryISOCode,
    sortCode: payload.sortCode ?? getSortCode(currentAccount.customFields),
  });

  return {
    addressLine1,
    city,
    postalCode,
    stateOrProvince,
    countryISOCode: payload.countryIsocode || countryISOCode,
    isActive: true,
    ...payload,
    ...(Object.keys(customFields).length
      ? { customFields: JSON.stringify(customFields) }
      : {}),
    bankAccountsId: currentAccount.bankAccountsId,
    businessesId: currentAccount.businessesId,
    treasuryBlobFileExtension: empty
      ? ''
      : data.treasuryBlobFileExtension ||
        currentAccount.treasuryBlobFileExtension,
    treasuryBlobFileName: empty
      ? ''
      : data.treasuryBlobFileName || currentAccount.treasuryBlobFileName,
    treasuryBlobFileSize: empty
      ? ''
      : data.treasuryBlobFileSize || currentAccount.treasuryBlobFileSize,
    treasuryBlobdocumentId: empty
      ? ''
      : data.treasuryBlobdocumentId || currentAccount.treasuryBlobdocumentId,
    accountNumberLast4: accountNumber ? accountNumber.slice(-4) : '',
    modifiedBy: emailAddress,
    banksIntegratorsId: integrator?.banksIntegratorsId || 0,
    integratorDirectoryId: directoryId,
  };
};

export const getBankAccountConnectionSchema = (t) =>
  yup
    .object()
    .shape({
      connectionName: stringRequired(t, 'bank:connectionName'),
    })
    .required();

export const getBankAccountInfoSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      accountName: stringRequired(t, 'bank:fields.bankName.label'),
      countryIsocode: stringRequired(t, 'common:fields.country.label'),
      addressLine1: stringRequired(t, 'common:fields.address.label'),
      city: validateCity(t),
      postalCode: validatePostalCode(t),
      stateOrProvince: validateState(t),
      sortCode: yup.string().when('countryIsocode', {
        is: CountryISOCodes.UnitedKingdom,
        then: (schema) =>
          schema
            // The input is masked (99-99-99); validate the raw digits only.
            .transform((value) => (value ? value.replace(/\D/g, '') : value))
            .required(
              t('common:validations.required', {
                name: t('bank:fields.sortCode.label'),
              }),
            )
            .min(6, t('bank:fields.sortCode.validation.length'))
            .max(6, t('bank:fields.sortCode.validation.length')),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
      bankAccountTypesId: yup
        .number()
        .nullable()
        .min(1, t('bank:fields.accountType.validation.required'))
        .required(t('bank:fields.accountType.validation.required')),
      routingNumber: yup.string().when('countryIsocode', {
        is: (val: string) =>
          val === CountryISOCodes.USA || val === CountryISOCodes.Canada,
        then: (schema) =>
          schema.trim().required(
            t('common:validations.required', {
              name: t('bank:fields.routingNumber.label'),
            }),
          ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
      accountNumber: yup.string().when('countryIsocode', {
        is: CountryISOCodes.UnitedKingdom,
        then: (schema) =>
          schema
            .trim()
            .required(
              t('common:validations.required', {
                name: t('bank:fields.accountNumber.label'),
              }),
            )
            .min(8, t('bank:fields.accountNumber.validation.length'))
            .max(8, t('bank:fields.accountNumber.validation.length')),
        otherwise: () => stringRequired(t, 'bank:fields.accountNumber.label'),
      }),
    })
    .required();
